import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, BookOpen, ArrowRight, Lock, RefreshCw, Flame, MessageCircle } from "lucide-react";
import { api, imgUrl } from "@/lib/api";

export default function Chronicles() {
  const [episodes, setEpisodes] = useState(null);
  const [limit, setLimit] = useState(5);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setEpisodes(null);
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const r = await api.get("/public/chronicles", { timeout: 15000 });
        setEpisodes(r.data.episodes || []);
        if (typeof r.data.free_episode_limit === "number") setLimit(r.data.free_episode_limit);
        return;
      } catch {
        if (attempt === 2) {
          setEpisodes([]);
          setError(true);
        } else {
          await new Promise((res) => setTimeout(res, 1200));
        }
      }
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(), 0);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="chronicles-bg min-h-screen" data-testid="chronicles-page">
      {/* Hero */}
      <header
        className="relative overflow-hidden pt-36 pb-24 lg:pt-44 lg:pb-32 px-6"
        data-testid="chronicles-hero"
      >
        <div className="absolute inset-0 chronicles-hero-glow pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[color:var(--gold)] mb-7">
            <Crown size={14} fill="var(--gold)" fillOpacity={0.25} strokeWidth={1.3} />
            Mob Mistress Entertainment
          </div>
          <h1 className="font-serif font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
            The <em className="italic" style={{ color: "var(--gold)" }}>Mob&nbsp;Mistress</em>
            <br />
            Chronicles
          </h1>
          <p className="mt-8 max-w-xl mx-auto text-base sm:text-lg text-white/65 leading-relaxed">
            A serialized digital memoir. Cinematic chapters of power, loyalty, and
            legacy — read like a book, lived like an empire.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.35em] text-white/40">
            <span>Power</span>
            <span className="w-1 h-1 rounded-full bg-[color:var(--gold)]" />
            <span>Loyalty</span>
            <span className="w-1 h-1 rounded-full bg-[color:var(--gold)]" />
            <span>Legacy</span>
          </div>
        </div>
      </header>

      {/* Episodes */}
      <section className="max-w-[1200px] mx-auto px-6 pb-32">
        {error ? (
          <div className="text-center py-20" data-testid="chronicles-error">
            <p className="text-white/60 mb-6">
              The chronicles didn&rsquo;t load just now. Check your connection and try again.
            </p>
            <button
              onClick={() => load()}
              className="btn-gold rounded-none inline-flex items-center gap-2 px-6 h-11 text-xs uppercase tracking-[0.25em]"
              data-testid="chronicles-retry"
            >
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        ) : episodes === null ? (
          <p className="text-center text-white/40 font-serif italic py-20">
            Loading the chronicles…
          </p>
        ) : episodes.length === 0 ? (
          <p className="text-center text-white/45 py-20" data-testid="chronicles-empty">
            The first chapter drops soon. Check back shortly.
          </p>
        ) : (
          <div className="space-y-10">
            {episodes.map((ep, idx) => {
              const locked = ep.episode_number > limit;
              return (
              <Link
                key={ep.id}
                to={`/chronicles/${ep.slug}`}
                className="chronicle-card group block"
                data-testid={`chronicle-card-${ep.slug}`}
                style={{ animationDelay: `${idx * 90}ms` }}
              >
                <div className="grid md:grid-cols-[minmax(0,420px)_1fr]">
                  <div className="relative overflow-hidden aspect-square md:aspect-auto">
                    {ep.cover_image ? (
                      <img
                        src={imgUrl(ep.cover_image)}
                        alt={ep.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#2a1822] to-[#0a0508]" />
                    )}
                    <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 bg-black/55 backdrop-blur-sm border border-[color:var(--gold)]/40 text-[10px] uppercase tracking-[0.3em] text-[color:var(--gold)]">
                      Episode {ep.episode_number}
                    </div>
                    {locked && (
                      <div
                        className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-[color:var(--gold)]/40 text-[10px] uppercase tracking-[0.25em] text-[color:var(--gold)]"
                        data-testid={`chronicle-locked-${ep.slug}`}
                      >
                        <Lock size={11} strokeWidth={1.8} /> Members
                      </div>
                    )}
                  </div>

                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    {ep.subtitle && (
                      <p className="text-[10px] uppercase tracking-[0.35em] text-white/45 mb-4">
                        {ep.subtitle}
                      </p>
                    )}
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter leading-[1.02] mb-5">
                      {ep.title}
                    </h2>
                    {ep.tagline && (
                      <p className="text-white/65 leading-relaxed text-base sm:text-lg italic font-serif max-w-xl">
                        “{ep.tagline}”
                      </p>
                    )}
                    <span className="mt-8 inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.3em] text-[color:var(--gold)] group-hover:gap-4 transition-all duration-300">
                      {locked ? <Lock size={14} strokeWidth={1.5} /> : <BookOpen size={14} strokeWidth={1.5} />}
                      {locked ? "Sign Up to Read" : "Read Episode"}
                      <ArrowRight size={14} strokeWidth={1.5} />
                    </span>
                    {(ep.reaction_count > 0 || ep.comment_count > 0) && (
                      <div
                        className="mt-6 flex items-center gap-5 text-[12px] text-white/45"
                        data-testid={`chronicle-engagement-${ep.slug}`}
                      >
                        {ep.reaction_count > 0 && (
                          <span className="inline-flex items-center gap-1.5" data-testid={`chronicle-reactions-count-${ep.slug}`}>
                            <Flame size={13} strokeWidth={1.8} style={{ color: "var(--gold)" }} />
                            <span className="tabular-nums">{ep.reaction_count}</span>
                          </span>
                        )}
                        {ep.comment_count > 0 && (
                          <span className="inline-flex items-center gap-1.5" data-testid={`chronicle-comments-count-${ep.slug}`}>
                            <MessageCircle size={13} strokeWidth={1.8} style={{ color: "var(--gold)" }} />
                            <span className="tabular-nums">{ep.comment_count}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
